// termofuse.jsx
import React from "react";
import TermOfUseLayout from "../../../components/TermOfUseLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function BrainIQTerms() {
  return (
    <TermOfUseLayout
      title="terms of use"
      appName="BrainIQ: Boost Your Mind"
      date="DECEMBER 2025"
    >
      <div className={s.title}>1) Acceptance of Terms</div>
      <p>
        These Terms of Use (the “Terms”) govern your access to and use of
        “BrainIQ: Boost Your Mind” (the “App”). By downloading, installing,
        accessing, or using the App, you agree to be bound by these Terms. If
        you do not agree, you must uninstall the App and discontinue all use.
        Where required by platform rules or local law, additional terms may
        apply; in case of conflict with App Store or Google Play terms, the
        platform terms may prevail to the extent required.
      </p>

      <div className={s.title}>2) Description of the App</div>
      <p>
        BrainIQ is a mobile application that offers quiz-style “brain tests”
        (for example, brain age, memory, attention, personality) and short
        daily exercises such as memory sequences, odd-one-out tasks, and tap
        speed challenges. The App may show test results, aggregate scores, text
        feedback, and optional AI-generated summaries, as well as maintain a
        history of completed tests and training sessions. Certain features,
        including full result reports and unlimited daily exercises, may require
        a paid subscription.
      </p>
      <p>
        The App is intended for entertainment and self-reflection only. It is
        not a medical tool, does not provide psychological, neurological, or
        clinical assessments, and must not be used to diagnose, treat, or
        monitor any disease, condition, or mental health issue.
      </p>

      <div className={s.title}>
        3) Entertainment and Non-Medical Purpose (Important Disclaimer)
      </div>
      <p>
        All tests, scores, “brain age” labels, attention or memory indicators,
        and personality-style feedback in the App are simplified models designed
        for fun, curiosity, and self-reflection. They are not scientifically
        validated clinical tools. Any references to “brain age,” “focus,”
        “memory,” or similar concepts are approximate and gamified, not precise
        measurements of cognitive function.
      </p>
      <p>
        The App does not provide medical, psychological, psychiatric, or
        therapeutic advice. You must not use the App to make decisions about
        your health, medication, therapy, or the health of others. If you have
        concerns about memory, attention, mood, or any cognitive or mental
        health issues, you should consult a qualified healthcare professional.
      </p>

      <div className={s.title}>4) Eligibility</div>
      <p>
        You may use the App only if you have the legal capacity to enter into a
        binding agreement under the laws of your jurisdiction. Where parental
        consent is required by law, a parent or guardian must approve use. By
        using the App, you confirm that you meet applicable age and eligibility
        requirements or have obtained the necessary consent. Parents or
        guardians remain responsible for supervising use by minors.
      </p>

      <div className={s.title}>5) Accounts, Guest Mode, and Progress</div>
      <p>
        The App may support use with or without an account (for example, using
        Firebase Authentication or similar services) and may store test history
        and preferences locally (for example, via Hive or SharedPreferences).
        Where account-based login is available, you are responsible for
        maintaining the confidentiality of your credentials and for all
        activities under your account. You must promptly notify us of any
        suspected unauthorized access.
      </p>
      <p>
        If you use the App without an account, certain data (such as history,
        onboarding flags, or local settings) may be stored only on your device
        and can be lost if you uninstall the App, reset your device, or clear
        storage. We cannot recover lost local data for you.
      </p>

      <div className={s.title}>6) Tests, Scoring, and AI Summaries</div>
      <p>
        The App provides quiz flows with multiple choice, single choice, or
        scale-based questions. Scoring is computed locally according to
        predefined rules and ranges. Test results may include score bands (for
        example, “Excellent”, “Good”, “Time to focus”) and descriptive text.
        Where enabled, the App may send aggregated test outcomes (such as test
        identifiers, score bands, and pattern choices) to a backend service in
        order to generate an AI-powered summary.
      </p>
      <p>
        AI-generated summaries and recommendations are created automatically and
        may occasionally be incomplete, repetitive, or not suitable for your
        specific circumstances. These summaries are general in nature, should be
        considered entertainment and reflection prompts only, and must not be
        interpreted as medical or psychological advice.
      </p>

      <div className={s.title}>7) Daily Training and Usage Limits</div>
      <p>
        Daily training modules (for example, memory sequence, odd-one-out,
        tap-speed exercises) are short games designed to encourage regular
        engagement. In the free tier, you may have limited daily attempts
        (for example, one free attempt per day for certain exercises), and the
        premium tier may unlock additional or unlimited sessions. Daily scores
        may be used to show progress over time in a simplified, gamified way.
      </p>
      <p>
        While regular practice may feel helpful or motivating, the App does not
        guarantee improvements in real-world cognition, academic performance,
        work productivity, or similar outcomes. Any perceived benefits are
        subjective and not assured.
      </p>

      <div className={s.title}>8) Free Tier, Paywall, and Premium Features</div>
      <p>
        The App may offer a free tier with limited features and a paid premium
        tier. Typical limitations in the free tier can include:
      </p>
      <ul>
        <li>restricted access to full test reports and detailed results;</li>
        <li>
          limited daily attempts in training games or limits on the number of
          tests or histories stored;
        </li>
        <li>
          paywalls shown before viewing some or all result screens, especially
          for first-time or advanced tests.
        </li>
      </ul>
      <p>
        Premium features may include full result reports, extended or unlimited
        daily training, extended history, additional test types, and other tools
        as described in the App and on the paywall screen. We may adjust limits,
        feature sets, and eligibility for free and premium tiers over time.
      </p>

      <div className={s.title}>9) Subscriptions, Trials, and Billing</div>
      <p>
        Premium features may be offered via auto-renewable subscriptions or
        other in-app purchases (“Subscriptions”), such as weekly, monthly, and
        yearly plans with a free trial (for example, a 3-day trial). Exact
        pricing, trial length, and benefits may vary by region and are shown
        within the App and in the app stores.
      </p>
      <p>
        Purchases are processed by Apple or Google via your store account; we do
        not receive your payment card numbers. If a free trial is offered, it
        will convert into a paid Subscription unless cancelled at least 24 hours
        before the end of the trial period in your account settings. You can
        manage or cancel Subscriptions directly in platform subscription
        settings. Deleting the App does not automatically cancel a Subscription.
        Refunds, grace periods, and other billing matters are governed by the
        policies of the relevant app store.
      </p>

      <div className={s.title}>10) Entitlements and Access Control</div>
      <p>
        Subscription status and eligibility may be managed using services such
        as RevenueCat and platform billing APIs. We rely on entitlement data
        (for example, product identifiers, trial eligibility, renewal and
        expiration status) to determine whether you have access to premium
        features such as full reports, additional tests, and unlimited daily
        exercises. An internet connection may be required to validate or refresh
        entitlements after long offline periods. If a transaction is reversed,
        refunded, or flagged as fraudulent, related entitlements may be
        suspended, limited, or revoked.
      </p>

      <div className={s.title}>11) History, Storage, and Data Loss</div>
      <p>
        Test history, training results, and related metadata may be stored
        locally on your device (for example, via Hive boxes) and/or remotely in
        services such as Firebase Firestore when authenticated. Local-only data
        may be lost if you uninstall the App, reset your device, or clear local
        storage. Remote data may not sync immediately due to network conditions
        or service outages.
      </p>
      <p>
        We do not guarantee that your entire history or prior AI summaries will
        be preserved indefinitely. You are responsible for exporting or
        capturing any information you consider important, and for verifying that
        significant results have been successfully saved.
      </p>

      <div className={s.title}>12) Acceptable Use</div>
      <p>
        You agree to use the App only for lawful, personal entertainment and
        self-reflection purposes and not to:
      </p>
      <ul>
        <li>
          treat scores or AI summaries as medical, psychological, or clinical
          evaluations;
        </li>
        <li>
          use the App to make or justify serious decisions affecting health,
          education, employment, or safety of yourself or others;
        </li>
        <li>
          attempt to reverse engineer, decompile, or extract the App&apos;s
          source code except where permitted by law;
        </li>
        <li>
          circumvent technical protections, usage limits, or paywalls separating
          free and premium features;
        </li>
        <li>
          use automated tools or scripts to artificially generate test runs,
          scores, or training data at scale;
        </li>
        <li>
          use the App to transmit spam, abusive, or illegal content, or to
          harass or harm others.
        </li>
      </ul>

      <div className={s.title}>13) Third-Party Services and AI Providers</div>
      <p>
        The App may rely on third-party services, including Firebase (for
        authentication, Firestore, Remote Config, Analytics, Messaging, and
        Crashlytics), RevenueCat (for subscription management), push
        notification providers, and external AI or model providers accessed
        through a secure backend. Technical and, where applicable, aggregated
        usage data and test outcome metadata may be sent to these providers to
        operate, secure, and improve the App.
      </p>
      <p>
        We require such providers by contract to handle data appropriately and
        only for permitted purposes, but they operate under their own terms and
        privacy policies. We are not responsible for service outages, rate
        limiting, or other issues outside our reasonable control.
      </p>

      <div className={s.title}>14) Analytics, Events, and Notifications</div>
      <p>
        We may collect analytics data to understand usage patterns and improve
        the App. Typical events can include onboarding_complete, test_started,
        test_finished (with test identifiers and score bands), paywall_shown,
        trial_started, purchase_completed, daily_game_started, and
        daily_game_result, as well as similar operational events. Data may be
        aggregated or pseudonymized where feasible.
      </p>
      <p>
        With your permission, the App may send push notifications—for example,
        reminders about new daily exercises, streak-related prompts, or general
        product updates. You can manage notification preferences in your device
        settings and, where available, within the App. Disabling notifications
        may affect reminders but does not disable core test and training
        functionality.
      </p>

      <div className={s.title}>15) No Warranties</div>
      <p>
        To the maximum extent permitted by law, the App is provided “as is” and
        “as available” without warranties of any kind, whether express or
        implied, including but not limited to implied warranties of
        merchantability, fitness for a particular purpose, and non-infringement.
        Without limiting the foregoing, we do not warrant that:
      </p>
      <ul>
        <li>
          scores, “brain age” estimates, or band labels accurately reflect your
          cognitive abilities or changes over time;
        </li>
        <li>
          daily training will improve real-life memory, attention, speed, or
          similar abilities;
        </li>
        <li>
          AI-generated summaries will be accurate, appropriate, or free from
          errors for all users and contexts;
        </li>
        <li>
          the App will operate without interruption, delay, or defects on all
          devices or OS versions.
        </li>
      </ul>

      <div className={s.title}>16) Limitation of Liability</div>
      <p>
        To the extent allowed by law, we are not liable for any indirect,
        incidental, consequential, or punitive damages, loss of profits, data
        loss (including loss of test history or scores), or business
        interruption arising from or related to your use of or inability to use
        the App, including reliance on scores, labels, or AI summaries. Our
        total aggregate liability to you for direct damages arising out of or
        relating to the App will be limited to the amount you paid for
        Subscriptions during the twelve (12) months prior to the claim, or the
        minimum amount permitted by applicable law if greater. Some
        jurisdictions do not allow certain limitations or exclusions, so parts
        of this section may not apply to you.
      </p>

      <div className={s.title}>17) Indemnification</div>
      <p>
        To the extent permitted by law, you agree to indemnify and hold harmless
        the publisher and its affiliates from any claims, liabilities, damages,
        losses, and expenses (including reasonable legal fees) arising out of or
        related to your misuse of the App, your reliance on non-medical scores
        or summaries for serious decisions, your breach of these Terms, or your
        violation of any rights of a third party.
      </p>

      <div className={s.title}>18) Termination</div>
      <p>
        We may suspend or terminate your access to the App, or to specific
        features, at any time if we reasonably believe you have violated these
        Terms, misused the App, or where required by law, platform rules, or
        security concerns. You may stop using the App at any time by
        uninstalling it and, where applicable, cancelling your Subscription via
        platform settings. Sections that by their nature should survive
        termination (for example, disclaimers, intellectual property, limitation
        of liability, and indemnification) will continue to apply after your
        access ends.
      </p>

      <div className={s.title}>19) Privacy and Data Protection</div>
      <p>
        Your use of the App is also governed by the separate Privacy Policy for
        BrainIQ: Boost Your Mind, which explains how information is collected,
        used, stored, and shared, including test data, device information, and
        analytics. In case of conflict between these Terms and the Privacy
        Policy regarding data processing, the Privacy Policy will govern how we
        handle personal data, while these Terms govern your use of the App and
        its features. You should review the Privacy Policy regularly to
        understand current practices.
      </p>

      <div className={s.title}>20) Changes, Governing Law, and Contact</div>
      <p>
        We may update the App, add or remove features (including changes to free
        and premium offerings), or modify these Terms from time to time. The
        “Last updated” date at the top of this document indicates when these
        Terms were last revised. Material changes may be highlighted in the App
        or communicated by other reasonable means. By continuing to use the App
        after changes take effect, you accept the updated Terms. If you do not
        agree to the revised Terms, you must stop using the App and uninstall
        it.
      </p>
      <p>
        Applicable law and dispute resolution mechanisms may depend on your
        country of residence and mandatory consumer protection rules. Where
        permitted, you agree to first contact us to attempt to resolve any
        dispute informally. Platform-specific dispute or refund procedures
        (Apple, Google) may also apply to billing matters. Nothing in these
        Terms limits any non-waivable rights you may have under local law.
      </p>
      <p>
        If you have questions about these Terms or the App, you can contact us
        at: <strong>alexwadek@gmail.com</strong>. We may require reasonable
        information to verify your identity before discussing account- or
        purchase-specific matters.
      </p>
    </TermOfUseLayout>
  );
}
